import { AxiosResponse } from 'axios';
import clsx from 'clsx';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PhoneValidationContainerInModal } from '@/components/Containers/PhoneValidationContainer';
import { ConfirmNeededPaymentModal, Spinner } from '@/components/Elements';
import { ActivationNeededModal } from '@/components/Elements/ActivationNeededModal';
import { ErrorModal } from '@/components/Elements/ErrorModal';
import { IncompletePaymentMethodFailedErrorModal } from '@/components/Elements/IncompletePaymentMethodFailedErrorModal';
import { IncompletePaymentRequiresActionErrorModal } from '@/components/Elements/IncompletePaymentRequiresActionErrorModal';
import { tinyModalStyles } from '@/components/Elements/Modal/customStyles';
import { PaymentErrorModal } from '@/components/Elements/PaymentErrorModal';
import { PaymentsModal } from '@/components/Elements/PaymentsModal';
import { PendingPaymentErrorModal } from '@/components/Elements/PendingPaymentErrorModal';
import { SecurityModal } from '@/components/Elements/SecurityModal';
import { SummaryPaymentModal } from '@/components/Elements/SummaryPaymentModal/SummaryPaymentModal';
import { UnpaidMeteredErrorModal } from '@/components/Elements/UnpaidMeteredErrorModal';
import { WaitProcessModal } from '@/components/Elements/WaitProcessModal';
import { iFormContactFields } from '@/components/Form/FormContact';
import { FormContactInModal } from '@/components/Form/FormContactInModal';
import { iFormPhoneValidationFields } from '@/components/Form/FormPhoneValidation';
import { iFormTokenCheckFields } from '@/components/Form/FormTokenCheck';
import { iFormUserAccountFields } from '@/components/Form/FormUserAccount';
import { FormUserAccountInModal } from '@/components/Form/FormUserAccountInModal';
import {
  MyRatePageContainer,
  MyRatePageContainerProps,
} from '@/components/PrivateContainers/MyRatePageContainer';
import {
  COUPONS_ENABLED,
  NOTIFICATION_CHECKOUT_REMINDER_ID,
  PATH_UPDATE_PAYMENT_METHOD,
} from '@/config';
import {
  ACTIVE,
  CANCELLED,
  DEFAULT_COUNTRY_ID,
  DEFAULT_PHONE,
  INCOMPLETE,
  REQUEST_SUCCESS,
} from '@/config/appConstants';
import { Country } from '@/features/countries';
import { MobileToken } from '@/features/mobileToken';
import { PaymentIntent, UpdatePaymentIntent } from '@/features/payment';
import { Province } from '@/features/provinces';
import { TinDocumentType } from '@/features/tinDocumentTypes';
import { useGroupBillingDetails } from '@/hooks/useGroupBillingDetails';
import { useMe } from '@/hooks/useMe';
import { useUserProducts } from '@/hooks/useUserProducts';
import { useNotificationStore } from '@/stores/notifications';
import { Currency, IntervalBillingMode, UserData, ProductTypes } from '@/types';
import {
  getOrigin,
  checkHasToShowPhoneValidationForm,
  checkHasToShowUserAccountForm,
  filterUserProductsByStatus,
  isManualValidationNeeded,
  userHasProducts,
  isCoreProduct,
  isAppSumo,
  getProductToUpdateFromUserCollection,
  isFreemiumProduct,
  isPrepaidProduct,
  isNewFreeUserFromMe,
  redirectMyRate,
  getPromotionCodeByCurrencyAndInterval,
  findPromotionCodeByGroupProductAndProduct,
  redirectToAndReload,
} from '@/utils/helpers';

export interface iMyRatePrivatePageProps extends Omit<MyRatePageContainerProps, 'onProductSelect'> {
  userData: UserData;
  onRequestInfoSubmit: (values: iFormContactFields) => void;
  onPlanCancel: (product: ProductTypes | null | undefined) => void;
  onPlanReactivate: (product: ProductTypes | null | undefined) => void;
  onAppsumoAction: () => void;
  countries: Country[];
  provinces: Province[];
  tinDocumentTypes: TinDocumentType[];
  countryOnChange: (formValues: any, countryId: string) => void;
  onSubmitFormUserAccount: (values: iFormUserAccountFields) => Promise<boolean>;
  onCancelButton: (product: ProductTypes | null | undefined) => void;
  getCreatePaymentIntent?: (
    productSelected: ProductTypes | null | undefined,
    userCurrency: Currency,
    intervalCount: IntervalBillingMode,
    preview: boolean,
    promotionCode: string,
    onErrorCallback: (status: number) => void
  ) => Promise<PaymentIntent | undefined>;

  getUpdatePaymentIntent?: (
    productSelected: ProductTypes,
    userCurrency: Currency,
    intervalCount: IntervalBillingMode,
    promotionCode: string | '',
    onErrorCallback: (status: number, urlRedirect?: string, detail?: string) => void
  ) => Promise<UpdatePaymentIntent | AxiosResponse | void>;

  onFinishURLRedirect?: string;
  completeCheckout?: boolean;
  autoCheckout?: boolean;
  mobileToken: MobileToken;
  onSubmitFormPhoneValidation: (values: iFormPhoneValidationFields) => void;
  onSubmitFormTokenCheck: (values: iFormTokenCheckFields) => void;
  onCancelIncompleteAction?: () => void;
  onConfirmUnpaidMeteredErrorAction: () => void;
}

export const MyRatePrivatePage = ({
  userProducts,
  userCurrency,
  userData,
  onRequestInfoSubmit,
  countries,
  provinces,
  tinDocumentTypes,
  countryOnChange,
  onSubmitFormUserAccount,
  onCancelButton,
  getCreatePaymentIntent,
  getUpdatePaymentIntent,
  onFinishURLRedirect,
  onPlanCancel,
  onPlanReactivate,
  onAppsumoAction,
  mobileToken,
  onSubmitFormPhoneValidation,
  onSubmitFormTokenCheck,
  completeCheckout = false,
  autoCheckout = false,
  mdirectorId,
  onCancelIncompleteAction,
  onConfirmUnpaidMeteredErrorAction,
}: iMyRatePrivatePageProps) => {
  const NO_CLOSE = 0;
  const REQUEST_PREVIEW = true;
  const { t } = useTranslation();
  const { dismissNotification } = useNotificationStore();
  const { setSelectedProduct, getSelectedUserProduct } = useUserProducts();
  const { getMe } = useMe();
  const { getBillingDetails } = useGroupBillingDetails();

  const {
    selectedProduct,
    selectedProductBillingMode,
    selectedProductIntervalBillingMode,
    selectedProductCurrency,
  } = getSelectedUserProduct();

  const userProductActive = filterUserProductsByStatus(userProducts, ACTIVE);
  const userProductCancelled = filterUserProductsByStatus(userProducts, CANCELLED);
  const userProductIncomplete = filterUserProductsByStatus(userProducts, INCOMPLETE);

  const [showSpinner, setShowSpinner] = useState(false);
  const [showPaymentError, setShowPaymentError] = useState<boolean>(false);
  const [statusPaymentError, setStatusPaymentError] = useState<number>(0);
  const [
    showIncompletePaymentRequiresActionErrorModal,
    setShowIncompletePaymentRequiresActionErrorModal,
  ] = useState<boolean | string>(false);
  const [
    showIncompletePaymentMethodFailedErrorModal,
    setShowIncompletePaymentMethodFailedErrorModal,
  ] = useState<boolean | string>(false);
  const [initCompleteCheckout, setInitCompleteCheckout] = useState(completeCheckout);

  const [confirmNeededPaymentModalIsOpen, setConfirmNeededPaymentModalIsOpen] = useState(false);
  const [summaryPaymentModalIsOpen, setSummaryPaymentModalIsOpen] = useState(false);
  const [upgradeCheckoutProcessModalIsOpen, setUpgradeCheckoutProcessModalIsOpen] = useState(false);
  const [upgradeCheckoutSuccessProcessModalIsOpen, setUpgradeCheckoutSuccessProcessModalIsOpen] =
    useState(false);
  const [upgradeCheckoutFailProcessModalIsOpen, setUpgradeCheckoutFailProcessModalIsOpen] =
    useState(false);
  const [statusUnpaidMeteredError, setStatusUnpaidMeteredError] = useState(false);
  const [statusPendingPaymentError, setStatusPendingPaymentError] = useState(false);

  const [invoiceSummaryData, setInvoiceSummaryData] = useState<any | null>(null);
  const { name, groupId, email } = userData;
  const billingDetails = getBillingDetails();
  const countryId = billingDetails?.countryId || DEFAULT_COUNTRY_ID;
  const phone = billingDetails?.phone || DEFAULT_PHONE;

  const checkHasToShowManualValidationModal = () => {
    const manualValidationNeeded = isManualValidationNeeded(getMe());
    const newFreeUser = isNewFreeUserFromMe(getMe());
    return autoCheckout && (manualValidationNeeded || newFreeUser);
  };

  const checkHasToShowSecurityModal = () => {
    return (
      autoCheckout &&
      (!selectedProduct || !confirmNeededPaymentModalIsOpen) &&
      !validationNeededModalIsOpen &&
      statusPaymentError > 0
    );
  };

  const checkRequestUserAccountModal = () => {
    return (
      autoCheckout &&
      checkHasToShowUserAccountForm(selectedProductCurrency, getMe(), billingDetails)
    );
  };

  const [requestInfoModalIsOpen, setRequestInfoModalIsOpen] = useState(false);
  const [requestUserAccountModalIsOpen, setRequestUserAccountModalIsOpen] = useState(
    checkRequestUserAccountModal()
  );
  const [requestPhoneValidationModalIsOpen, setRequestPhoneValidationModalIsOpen] = useState(false);
  const [validationNeededModalIsOpen, setValidationNeededModalIsOpen] = useState(
    checkHasToShowManualValidationModal()
  );
  const [securityModalIsOpen, setSecurityModalIsOpen] = useState(checkHasToShowSecurityModal());

  const onProductSelect = (product: ProductTypes) => {
    setSelectedProduct(product);
    const { selectedProductCurrency } = getSelectedUserProduct();
    const userPromotionCode = ''; //findPromotionCodeByGroupProductAndProduct(userProducts, product);
    dismissNotification(`${NOTIFICATION_CHECKOUT_REMINDER_ID}${product?.mdirectorId}`);

    if (!userHasProducts(userProducts)) {
      onRequestInfo(product);
      return;
    }

    if (checkHasToShowUserAccountForm(selectedProductCurrency, getMe(), billingDetails)) {
      if (checkHasToShowPhoneValidationForm(mobileToken, getMe())) {
        onRequestPhoneValidationModalOpen();
        return;
      }

      onRequestUserAccountModalOpen();
      return;
    }

    if (isManualValidationNeeded(getMe()) || isNewFreeUserFromMe(getMe())) {
      onShowValidationNeededModalOpen();
      return;
    }

    onRequestPaymentModalOpen(selectedProductCurrency, product, userPromotionCode);
  };

  const closeAllModals = (omit?: string) => {
    omit !== 'InfoModal' && setRequestInfoModalIsOpen(false);
    omit !== 'UserAccountModal' && setRequestUserAccountModalIsOpen(false);
    omit !== 'PhoneValidationModal' && setRequestPhoneValidationModalIsOpen(false);
    omit !== 'ValidationNeededModal' && setValidationNeededModalIsOpen(false);
    omit !== 'SecurityModal' && setSecurityModalIsOpen(false);
    omit !== 'ConfirmNeededPaymentModal' && setConfirmNeededPaymentModalIsOpen(false);
    omit !== 'SummaryPaymentModal' && setSummaryPaymentModalIsOpen(false);

    if (omit !== 'WaitProcessModal') {
      setUpgradeCheckoutProcessModalIsOpen(false);
      setUpgradeCheckoutSuccessProcessModalIsOpen(false);
    }
    // This modal can not be omitted
    setUpgradeCheckoutFailProcessModalIsOpen(false);
  };

  const onRequestInfoClose = () => {
    closeAllModals();
  };

  const onRequestInfo = (product: ProductTypes | null) => {
    setSelectedProduct(product);
    setRequestInfoModalIsOpen(true);
    closeAllModals('InfoModal');
  };

  const onDefaultRequestInfoSubmit = (values: iFormContactFields) => {
    onRequestInfoSubmit(values);
    onRequestInfoClose();
  };

  const onRequestPaymentModalClose = () => {
    closeAllModals();
  };

  const onRequestCloseActivationNeededModal = () => {
    closeAllModals();
  };

  const onRequestCloseConfirmNeededPaymentModal = () => {
    closeAllModals();
    setStatusUnpaidMeteredError(false);
    setStatusPendingPaymentError(false);
  };

  const onUpgradeCheckoutErrorCallback = (
    status: number,
    urlRedirect?: string,
    detail?: string
  ) => {
    closeAllModals();
    if (status === 402 && detail == 'requires_action')
      return setShowIncompletePaymentRequiresActionErrorModal(
        urlRedirect || PATH_UPDATE_PAYMENT_METHOD
      );
    if (status === 402 && detail == 'payment_method_failed')
      return setShowIncompletePaymentMethodFailedErrorModal(
        urlRedirect || PATH_UPDATE_PAYMENT_METHOD
      );
    if (
      status === 409 &&
      detail &&
      detail === 'There are unpaid metered items in this subscription'
    )
      return setStatusUnpaidMeteredError(status > 0);

    if (
      status === 409 &&
      detail &&
      detail === 'There are pending payments for the current subscription'
    )
      return setStatusPendingPaymentError(status > 0);
    if (
      status === 409 &&
      detail &&
      detail === 'Cannot update a subscription with an incomplete payment'
    )
      return setStatusPaymentError(status);

    setUpgradeCheckoutFailProcessModalIsOpen(true);
  };

  const onUpgradeCheckoutModalClose = () => {
    closeAllModals();
  };

  const onActionConfirmNeededPaymentModal = async (promotionCode: string | '') => {
    if (
      !getUpdatePaymentIntent ||
      !selectedProduct ||
      !selectedProductCurrency ||
      !selectedProductIntervalBillingMode
    ) {
      return;
    }

    setUpgradeCheckoutProcessModalIsOpen(true);
    if (!promotionCode)
      promotionCode = getPromotionCodeByCurrencyAndInterval(
        selectedProduct.prices,
        selectedProductCurrency,
        selectedProductIntervalBillingMode
      );

    const updatePaymentResponse = await getUpdatePaymentIntent(
      selectedProduct,
      selectedProductCurrency,
      selectedProductIntervalBillingMode,
      promotionCode,
      onUpgradeCheckoutErrorCallback
    );

    if (updatePaymentResponse?.status === REQUEST_SUCCESS) {
      setUpgradeCheckoutProcessModalIsOpen(false);
      setUpgradeCheckoutSuccessProcessModalIsOpen(true);
    }
  };

  const onActionConfirmSummaryPaymentModal = async (promotionCode: string) => {
    await generatePaymentIntent(
      selectedProductCurrency,
      selectedProduct,
      selectedProductIntervalBillingMode,
      false,
      promotionCode
    );
  };

  const onRequestPaymentModalOpen = async (
    currency: Currency,
    product: ProductTypes | null,
    promotionCode: string | ''
  ) => {
    // We print the "UpdateProduct" modal always user has a product non freemium.
    if (
      userProducts &&
      userProducts.length > 0 &&
      !isFreemiumProduct(userProductActive?.product) &&
      !isPrepaidProduct(product)
    ) {
      setConfirmNeededPaymentModalIsOpen(true);
      closeAllModals('ConfirmNeededPaymentModal');
    } else {
      const invoiceSummaryData = await generatePaymentIntent(
        currency,
        product,
        selectedProductIntervalBillingMode,
        REQUEST_PREVIEW,
        promotionCode
      );

      setInvoiceSummaryData(invoiceSummaryData);
      if (invoiceSummaryData) setSummaryPaymentModalIsOpen(true);
      setSummaryPaymentModalIsOpen(true);
      closeAllModals('SummaryPaymentModal');
    }
  };

  const onShowValidationNeededModalOpen = () => {
    setValidationNeededModalIsOpen(true);
    closeAllModals('ValidationNeededModal');
  };

  const onRequestUserAccountModalClose = () => {
    closeAllModals();
  };

  const onRequestUserAccountModalOpen = () => {
    setRequestUserAccountModalIsOpen(true);
    closeAllModals('UserAccountModal');
  };

  const onDefaultFormUserAccountSubmit = async (values: iFormUserAccountFields) => {
    const update = await onSubmitFormUserAccount(values);
    if (!update) {
      return false;
    }
    onRequestUserAccountModalClose();

    const userPromotionCode = findPromotionCodeByGroupProductAndProduct(
      userProducts,
      selectedProduct
    );

    isManualValidationNeeded(getMe()) || isNewFreeUserFromMe(getMe())
      ? onShowValidationNeededModalOpen()
      : onRequestPaymentModalOpen(userCurrency, selectedProduct, userPromotionCode);
    return true;
  };

  const onRequestPhoneValidationModalOpen = () => {
    setRequestPhoneValidationModalIsOpen(true);
    closeAllModals('PhoneValidationModal');
  };

  const onRequestPhoneValidationModalClose = () => {
    closeAllModals();
  };

  const onRequestInfoWithoutProduct = () => {
    setRequestInfoModalIsOpen(true);
    closeAllModals('InfoModal');
  };

  const onRequestSecurityModalClose = () => {
    closeAllModals();
  };

  const onErrorCallback = (status: number) => {
    closeAllModals();
    if (status === 503 || status === 500 || status === 402) return setStatusPaymentError(status);
    setShowPaymentError(true);
  };

  const hasToRedirectStripeCheckOut = (url: string) => {
    window.location.assign(url);
  };

  const generatePaymentIntent = async (
    currency: Currency,
    product: ProductTypes | null,
    intervalCount: IntervalBillingMode,
    preview = false,
    promotionCode: string | ''
  ) => {
    if (
      getCreatePaymentIntent &&
      product &&
      (userHasProducts(userProducts) || !isCoreProduct(product))
    ) {
      setShowSpinner(true);

      const paymentIntent = await getCreatePaymentIntent(
        product,
        currency,
        intervalCount,
        preview,
        promotionCode,
        onErrorCallback
      );

      setShowSpinner(false);

      if (paymentIntent) {
        if (preview) {
          return paymentIntent;
        }

        if (paymentIntent.sessionUrl) {
          hasToRedirectStripeCheckOut(paymentIntent.sessionUrl);
        }
      }
    }
  };

  const onDefaultSubmitFormTokenCheck = (values: iFormTokenCheckFields) => {
    onSubmitFormTokenCheck && onSubmitFormTokenCheck(values);
    setRequestUserAccountModalIsOpen(true);
  };

  const onDefaultConfirmPendingPaymentErrorAction = () => {
    window.location.assign(PATH_UPDATE_PAYMENT_METHOD);
    onRequestCloseConfirmNeededPaymentModal && onRequestCloseConfirmNeededPaymentModal();
  };
  const onDefaultConfirmUnpaidMeteredErrorAction = () => {
    onConfirmUnpaidMeteredErrorAction && onConfirmUnpaidMeteredErrorAction();

    onRequestCloseConfirmNeededPaymentModal && onRequestCloseConfirmNeededPaymentModal();
  };

  const renderSpinner = () => (
    <PaymentsModal
      closeButton={false}
      isOpen={showSpinner}
      style={tinyModalStyles}
      shouldCloseOnOverlayClick={false}
    >
      <div className="flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    </PaymentsModal>
  );

  const renderPaymentError = () => (
    <PaymentErrorModal
      titleTag="PaymentForm.paymentFailTitle"
      isOpen={showPaymentError}
      onRequestClose={() => {
        setShowPaymentError(false);
      }}
      shouldRedirect={true}
    />
  );

  const renderIncompletePaymentRequiresActionErrorModal = () => (
    <IncompletePaymentRequiresActionErrorModal
      isOpen={showIncompletePaymentRequiresActionErrorModal != false}
      onConfirmAction={onDefaultConfirmIncompleteAction}
      onCancelAction={onDefaultCancelIncompleteAction}
    />
  );

  const renderIncompletePaymentMethodFailedErrorModal = () => (
    <IncompletePaymentMethodFailedErrorModal
      isOpen={showIncompletePaymentMethodFailedErrorModal != false}
      onConfirmAction={onDefaultConfirmIncompleteAction}
      onCancelAction={onDefaultCancelIncompleteAction}
    />
  );

  const renderPaymentUpdateError = () => (
    <PaymentErrorModal
      titleTag="PaymentForm.upgradeFailTitle"
      isOpen={upgradeCheckoutFailProcessModalIsOpen}
      onRequestClose={onUpgradeCheckoutModalClose}
    />
  );
  const renderStatusPaymentErrorModal = () => (
    <ErrorModal
      status={statusPaymentError}
      onRequestClose={() => {
        setStatusPaymentError(0);
        redirectMyRate();
      }}
    />
  );

  const renderStatusUnpaidMeteredErrorModal = () => (
    <UnpaidMeteredErrorModal
      isOpen={statusUnpaidMeteredError != false}
      onConfirmAction={onDefaultConfirmUnpaidMeteredErrorAction}
      onCancelAction={onRequestCloseConfirmNeededPaymentModal}
    />
  );

  const renderStatusPendingPaymentErrorModal = () => (
    <PendingPaymentErrorModal
      isOpen={statusPendingPaymentError != false}
      onConfirmAction={onDefaultConfirmPendingPaymentErrorAction}
      onCancelAction={onRequestCloseConfirmNeededPaymentModal}
    />
  );

  const timeoutCheckoutSuccessWaitProcess = () => {
    onFinishURLRedirect && window.location.assign(onFinishURLRedirect);
  };

  const onDefaultPlanCancel = async (product: ProductTypes | null | undefined) => {
    closeAllModals();
    await onPlanCancel(product);
  };

  const onDefaultPlanReactive = async (product: ProductTypes | null | undefined) => {
    closeAllModals();
    await onPlanReactivate(product);
  };

  const onDefaultConfirmIncompleteAction = () => {
    redirectToAndReload(showIncompletePaymentRequiresActionErrorModal as string);
    setShowIncompletePaymentRequiresActionErrorModal(false);
    setShowIncompletePaymentMethodFailedErrorModal(false);
  };

  const onDefaultCancelIncompleteAction = async () => {
    closeAllModals();
    onCancelIncompleteAction && (await onCancelIncompleteAction());
    setShowIncompletePaymentRequiresActionErrorModal(false);
    setShowIncompletePaymentMethodFailedErrorModal(false);
  };

  const phoneValidationNeeded: boolean = checkHasToShowPhoneValidationForm(mobileToken, getMe());
  const oldUserProduct = getProductToUpdateFromUserCollection(userProductActive, userProducts);

  if (initCompleteCheckout) {
    setInitCompleteCheckout(false);
    if (completeCheckout && selectedProduct) {
      onProductSelect(selectedProduct);
    }
  }

  return (
    <div className="flex justify-center  w-full mt-2 lg:mt-10 bg-md-lightGray">
      <div className={clsx('w-full bg-md-white mb-20')}>
        {renderSpinner()}
        {showPaymentError && renderPaymentError()}
        {showIncompletePaymentRequiresActionErrorModal &&
          renderIncompletePaymentRequiresActionErrorModal()}
        {showIncompletePaymentMethodFailedErrorModal &&
          renderIncompletePaymentMethodFailedErrorModal()}
        {statusPaymentError > 0 && renderStatusPaymentErrorModal()}
        {upgradeCheckoutFailProcessModalIsOpen && renderPaymentUpdateError()}
        {statusUnpaidMeteredError && renderStatusUnpaidMeteredErrorModal()}
        {statusPendingPaymentError && renderStatusPendingPaymentErrorModal()}

        <WaitProcessModal
          timeInMS={NO_CLOSE}
          title={t('Forms.UpgradeCheckout')}
          description={t('Forms.UpgradeCheckoutDescription')}
          isOpen={upgradeCheckoutProcessModalIsOpen}
        />
        <WaitProcessModal
          title={t('Forms.UpgradeCheckoutSuccess')}
          description={t('Forms.UpgradeCheckoutDescription')}
          isOpen={upgradeCheckoutSuccessProcessModalIsOpen}
          timeoutCallback={timeoutCheckoutSuccessWaitProcess}
        />
        {selectedProduct && phoneValidationNeeded && (
          <PhoneValidationContainerInModal
            isOpen={requestPhoneValidationModalIsOpen}
            mobileToken={mobileToken}
            userData={userData}
            onRequestInfo={onRequestInfo}
            onSubmitFormPhoneValidation={onSubmitFormPhoneValidation}
            onSubmitFormTokenCheck={onDefaultSubmitFormTokenCheck}
            onRequestClose={onRequestPhoneValidationModalClose}
          />
        )}
        {selectedProduct && !phoneValidationNeeded && (
          <FormUserAccountInModal
            isOpen={requestUserAccountModalIsOpen}
            actionButton={t('Forms.Checkout.next')}
            countries={countries}
            provinces={provinces}
            tinDocumentTypes={tinDocumentTypes}
            groupId={groupId}
            countryOnChange={countryOnChange}
            showPaymentCheckout={true}
            showAgreementsCheck={isAppSumo(getMe())}
            product={selectedProduct}
            currency={userCurrency}
            onSubmit={onDefaultFormUserAccountSubmit}
            onRequestClose={onRequestPaymentModalClose}
            onCancelButton={onCancelButton}
            onRequestInfo={onRequestInfo}
            billingMode={selectedProductBillingMode}
          />
        )}
        <ActivationNeededModal
          isOpen={validationNeededModalIsOpen}
          onRequestClose={onRequestCloseActivationNeededModal}
        />
        <SecurityModal
          isOpen={securityModalIsOpen}
          onRequestClose={onRequestSecurityModalClose}
          onClick={onRequestInfoWithoutProduct}
        />
        <FormContactInModal
          isOpen={requestInfoModalIsOpen}
          shouldCloseOnOverlayClick={true}
          groupId={groupId}
          name={name}
          email={email}
          countryId={countryId}
          phone={phone}
          origin={getOrigin(selectedProduct, selectedProductBillingMode, 'MyRatePrivatePage')}
          product={getOrigin(
            userProductActive?.product || userProductCancelled?.product || selectedProduct
          )}
          onSubmit={onDefaultRequestInfoSubmit}
          onRequestClose={onRequestInfoClose}
        />
        {confirmNeededPaymentModalIsOpen && oldUserProduct && selectedProduct && (
          <ConfirmNeededPaymentModal
            isOpen={confirmNeededPaymentModalIsOpen}
            onRequestClose={onRequestCloseConfirmNeededPaymentModal}
            onActionButton={onActionConfirmNeededPaymentModal}
            oldUserProduct={oldUserProduct}
            newUserProduct={selectedProduct}
            newCurrency={selectedProductCurrency}
            newIntervalCount={selectedProductIntervalBillingMode}
            isCouponEnabled={COUPONS_ENABLED}
            onConfirmUnpaidMeteredErrorAction={onDefaultConfirmUnpaidMeteredErrorAction}
          />
        )}
        {summaryPaymentModalIsOpen &&
          getCreatePaymentIntent &&
          selectedProduct &&
          invoiceSummaryData && (
            <SummaryPaymentModal
              isOpen={summaryPaymentModalIsOpen}
              onRequestClose={onRequestCloseConfirmNeededPaymentModal}
              onActionButton={onActionConfirmSummaryPaymentModal}
              newUserProduct={selectedProduct}
              newCurrency={selectedProductCurrency}
              newIntervalCount={selectedProductIntervalBillingMode}
              initialInvoiceUpdateSummaryData={invoiceSummaryData}
              isCouponEnabled={COUPONS_ENABLED}
              actionButtonLabel={t('Forms.Checkout.reminder.goToCheckoutShort')}
              getCreatePaymentIntent={getCreatePaymentIntent}
            />
          )}
        <MyRatePageContainer
          userData={userData}
          userProducts={userProducts}
          userCurrency={userCurrency}
          onRequestInfo={onRequestInfo}
          onProductSelect={onProductSelect}
          onPlanCancel={onDefaultPlanCancel}
          onPlanReactivate={onDefaultPlanReactive}
          onAppsumoAction={onAppsumoAction}
          mdirectorId={mdirectorId}
          onPendingButtonAction={() => {
            console.info('onPendingButtonAction...');
          }}
          onIncompleteAction={() => {
            redirectToAndReload(
              userProductIncomplete?.groupProduct.completePaymentUrl || PATH_UPDATE_PAYMENT_METHOD
            );
          }}
          onCancelIncompleteAction={onDefaultCancelIncompleteAction}
        />
      </div>
    </div>
  );
};
